import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { Timestamp, initializeFirestore, persistentLocalCache, persistentMultipleTabManager, collection, addDoc, getDocs, query, limit } from 'firebase/firestore';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { getStorage } from 'firebase/storage';

// Firebase configuration - using environment variables for security
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "the-estate-hub.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "the-estate-hub",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "the-estate-hub.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "904129270236",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || ""
};

// Validate configuration
if (!firebaseConfig.apiKey || !firebaseConfig.appId) {
  console.error('❌ [Firebase] Missing required configuration!');
  console.error('❌ [Firebase] API Key:', firebaseConfig.apiKey ? 'SET' : 'MISSING');
  console.error('❌ [Firebase] App ID:', firebaseConfig.appId ? 'SET' : 'MISSING');
  console.error('❌ [Firebase] Check your .env file for VITE_FIREBASE_* variables');
}

// Log Firebase configuration status (without sensitive data)
console.log('🔧 [Firebase] Initializing Firebase...');
console.log('🔧 [Firebase] Project ID:', firebaseConfig.projectId);
console.log('🔧 [Firebase] Auth Domain:', firebaseConfig.authDomain);
console.log('🔧 [Firebase] Storage Bucket:', firebaseConfig.storageBucket);
console.log('🔧 [Firebase] API Key:', firebaseConfig.apiKey ? 'SET (' + firebaseConfig.apiKey.substring(0, 10) + '...)' : 'MISSING');
console.log('🔧 [Firebase] App ID:', firebaseConfig.appId ? 'SET' : 'MISSING');
console.log('🔧 [Firebase] Messaging Sender ID:', firebaseConfig.messagingSenderId);

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Log auth initialization
console.log('🔧 [Firebase] Auth initialized:', !!auth);
console.log('🔧 [Firebase] Initial currentUser:', auth.currentUser ? auth.currentUser.email : 'null');

// Listen for auth state changes
auth.onAuthStateChanged((user) => {
  console.log('🔐 [Firebase Auth] State changed:', user ? `User: ${user.email}` : 'No user');
});

// Initialize Firestore with persistence for better offline support
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  })
});

// Initialize Analytics only in production-like environments
export let analytics: Analytics | undefined;
if (process.env.NODE_ENV !== 'development' && 
    !window.location.host.includes('stackblitz') && 
    !window.location.host.includes('webcontainer')) {
  analytics = getAnalytics(app);
}

// Initialize Firebase Storage
// To use Cloud Storage:
// - gs:// URL format: gs://the-estate-hub.appspot.com/path/to/file
// - HTTP URL format: https://firebasestorage.googleapis.com/v0/b/the-estate-hub.appspot.com/o/path%2Fto%2Ffile
export const storage = getStorage(app);

// Test Firebase connection
export const testFirebaseConnection = async () => {
  try {
    console.log('🧪 [Firebase] Testing connection...');
    
    // Test 1: Check if Firestore is initialized
    if (!db) {
      throw new Error('Firestore database not initialized');
    }
    console.log('✅ [Firebase] Firestore initialized');
    
    // Test 2: Check if Auth is initialized
    if (!auth) {
      throw new Error('Firebase Auth not initialized');
    }
    console.log('✅ [Firebase] Auth initialized');
    
    // Test 3: Try to read a test collection
    try {
      const testQuery = query(collection(db, 'users'), limit(1));
      await getDocs(testQuery);
      console.log('✅ [Firebase] Can read from Firestore');
    } catch (error: unknown) {
      console.warn('⚠️ [Firebase] Cannot read from Firestore:', error instanceof Error ? error.message : error);
      if ((error as { code?: string }).code === 'permission-denied') {
        console.error('❌ [Firebase] Permission denied - check Firestore rules!');
      }
    }
    
    // Test 4: Check current user
    if (auth.currentUser) {
      console.log('✅ [Firebase] User authenticated:', auth.currentUser.email);
      console.log('✅ [Firebase] User UID:', auth.currentUser.uid);
    } else {
      console.log('ℹ️ [Firebase] No user authenticated (this is OK if not logged in)');
    }
    
    console.log('✅ [Firebase] Connection test completed');
    return { success: true, message: 'Firebase connection OK' };
  } catch (error: unknown) {
    console.error('❌ [Firebase] Connection test failed:', error);
    return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Auto-test connection on module load (only in browser)
if (typeof window !== 'undefined') {
  // Wait a bit before testing to ensure Firebase is fully initialized
  setTimeout(() => {
    testFirebaseConnection();
  }, 1000);
}

// Initialize transactions collection if it doesn't exist
export const initializeTransactionsCollection = async () => {
  try {
    console.log('Sprawdzanie kolekcji transactions...');
    // Check if collection exists by trying to get a document
    const transactionsQuery = query(collection(db, 'transactions'), limit(1));
    const snapshot = await getDocs(transactionsQuery);
    
    console.log('Znaleziono dokumentów:', snapshot.size);
    
    if (snapshot.empty) {
      console.log('Kolekcja transactions jest pusta lub nie istnieje. Inicjalizacja...');
      // Collection doesn't exist or is empty, initialize it with a dummy document
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log('Tworzenie przykładowego dokumentu dla użytkownika:', currentUser.uid);
        try {
          const dummyTransaction = {
            title: 'Inicjalizacja kolekcji',
            description: 'Ten dokument został utworzony automatycznie w celu inicjalizacji kolekcji',
            status: 'draft' as const,
            user_id: currentUser.uid,
            created_at: Timestamp.now(),
            updated_at: Timestamp.now()
          };
          
          await addDoc(collection(db, 'transactions'), dummyTransaction);
          console.log('Pomyślnie zainicjalizowano kolekcję transactions');
        } catch (error) {
          console.error('Błąd podczas inicjalizacji kolekcji transactions:', error);
        }
      } else {
        console.log('Brak zalogowanego użytkownika, nie można zainicjalizować kolekcji');
      }
    } else {
      console.log('Kolekcja transactions już istnieje');
    }
  } catch (error) {
    console.error('Błąd podczas sprawdzania kolekcji transactions:', error);
  }
};

// Initialize chats collection if it doesn't exist
export const initializeChatsCollection = async () => {
  try {
    console.log('Sprawdzanie kolekcji chats...');
    // Check if collection exists by trying to get a document
    const chatsQuery = query(collection(db, 'chats'), limit(1));
    const snapshot = await getDocs(chatsQuery);
    
    console.log('Znaleziono dokumentów w kolekcji chats:', snapshot.size);
    
    if (snapshot.empty) {
      console.log('Kolekcja chats jest pusta lub nie istnieje. Inicjalizacja...');
      // Collection doesn't exist or is empty, initialize it with a dummy document
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log('Tworzenie przykładowego czatu dla użytkownika:', currentUser.uid);
        try {
          const dummyChat = {
            participants: [currentUser.uid],
            participantNames: {
              [currentUser.uid]: currentUser.displayName || currentUser.email || 'Użytkownik'
            },
            participantPhotos: {
              [currentUser.uid]: currentUser.photoURL || ''
            },
            isGroup: false,
            created_at: Timestamp.now(),
            updated_at: Timestamp.now(),
            createdAt: Timestamp.now().toDate().toISOString(),
            updatedAt: Timestamp.now().toDate().toISOString(),
            unreadCount: {}
          };
          
          await addDoc(collection(db, 'chats'), dummyChat);
          console.log('Pomyślnie zainicjalizowano kolekcję chats');
        } catch (error) {
          console.error('Błąd podczas inicjalizacji kolekcji chats:', error);
        }
      } else {
        console.log('Brak zalogowanego użytkownika, nie można zainicjalizować kolekcji');
      }
    } else {
      console.log('Kolekcja chats już istnieje');
    }
  } catch (error) {
    console.error('Błąd podczas sprawdzania kolekcji chats:', error);
  }
};

// Initialize messages collection if it doesn't exist
export const initializeMessagesCollection = async () => {
  try {
    console.log('Sprawdzanie kolekcji messages...');
    // Check if collection exists by trying to get a document
    const messagesQuery = query(collection(db, 'messages'), limit(1));
    const snapshot = await getDocs(messagesQuery);
    
    console.log('Znaleziono dokumentów w kolekcji messages:', snapshot.size);
    
    if (snapshot.empty) {
      console.log('Kolekcja messages jest pusta lub nie istnieje. Inicjalizacja...');
      // Collection doesn't exist or is empty, initialize it with a dummy document
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log('Tworzenie przykładowej wiadomości dla użytkownika:', currentUser.uid);
        try {
          // First ensure we have a chat to reference
          const chatsQuery = query(collection(db, 'chats'), limit(1));
          const chatSnapshot = await getDocs(chatsQuery);
          
          let chatId;
          if (chatSnapshot.empty) {
            // Create a chat first
            await initializeChatsCollection();
            const newChatsQuery = query(collection(db, 'chats'), limit(1));
            const newChatSnapshot = await getDocs(newChatsQuery);
            if (!newChatSnapshot.empty) {
              chatId = newChatSnapshot.docs[0].id;
            } else {
              console.log('Nie udało się utworzyć czatu, nie można zainicjalizować kolekcji messages');
              return;
            }
          } else {
            chatId = chatSnapshot.docs[0].id;
          }
          
          const dummyMessage = {
            chatId: chatId,
            senderId: currentUser.uid,
            senderName: currentUser.displayName || currentUser.email || 'Użytkownik',
            text: 'Inicjalizacja kolekcji messages',
            timestamp: Timestamp.now(),
            status: 'sent',
            type: 'text',
            created_at: Timestamp.now(),
            updated_at: Timestamp.now()
          };
          
          await addDoc(collection(db, 'messages'), dummyMessage);
          console.log('Pomyślnie zainicjalizowano kolekcję messages');
        } catch (error) {
          console.error('Błąd podczas inicjalizacji kolekcji messages:', error);
        }
      } else {
        console.log('Brak zalogowanego użytkownika, nie można zainicjalizować kolekcji');
      }
    } else {
      console.log('Kolekcja messages już istnieje');
    }
  } catch (error) {
    console.error('Błąd podczas sprawdzania kolekcji messages:', error);
  }
};

// Initialize all messaging collections
export const initializeMessagingCollections = async () => {
  try {
    console.log('Inicjalizacja kolekcji wiadomości...');
    await initializeChatsCollection();
    await initializeMessagesCollection();
    console.log('Inicjalizacja kolekcji zakończona pomyślnie');
  } catch (error) {
    console.error('Błąd podczas inicjalizacji kolekcji:', error);
  }
};

// Funkcja pomocnicza do logowania informacji o indeksach
export const logIndexRequirements = () => {
  console.log(`
    Informacja: Indeksy Firebase są już skonfigurowane.
    
    Jeśli pojawią się problemy z zapytaniami, sprawdź czy masz następujące indeksy w Firebase:
    
    1. Kolekcja: chats
       - Pole: participants (array-contains)
       - Pole: updated_at (order)
       
    2. Kolekcja: messages
       - Pole: chatId (equal)
       - Pole: timestamp (order)
       
    3. Kolekcja: messages
       - Pole: chatId (equal)
       - Pole: senderId (not-equal)
       - Pole: status (not-equal)
  `);
};

// Export types
export interface Client {
  id: string;
  name?: string | null; // Dodane pole nazwa
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
  client_type: 'buyer' | 'seller';
  locations: string[];
  price_min: number | string | null;
  price_max: number | string | null;
  agent_id: string | null;
  agent_name: string | null;
  tags: string[];
  status: string[]; // Tablica statusów: 'aktywny' | 'wstrzymany' | 'do_sprawdzenia' | 'inwestor' | 'deweloper' | 'relacyjny'
  is_public?: boolean; // Czy klient jest widoczny w Panelu Właściciela
  // Search criteria for buyer matching
  estate_type?: string | null;    // '1'=Dom, '2'=Mieszkanie, '3'=Działka, '4'=Lokal
  rooms_min?: number | null;
  rooms_max?: number | null;
  area_min?: number | null;
  area_max?: number | null;
}

export interface ClientOffer {
  id: string;
  client_id: string;
  offer_id: string;
  created_at: string;
  user_id: string;
  notes?: string | null;
  status: string;
}

export interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderPhotoURL?: string;
  text: string;
  timestamp: string;
  isRead: boolean;
  chatId: string;
  roomId?: string | null;
  isDeleted?: boolean;
}

export interface Chat {
  id: string;
  participants: string[];
  participantNames: Record<string, string>;
  participantPhotos?: Record<string, string>;
  isGroup?: boolean;
  groupName?: string;
  createdBy?: string;
  lastMessage?: {
    text: string;
    timestamp: string;
    isRead: boolean;
    senderId: string;
    id: string;
    isDeleted?: boolean;
  };
  createdAt: string;
  updatedAt: string;
  groupPhoto?: string;
  isMainChat?: boolean;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
  priority: 'high' | 'normal' | 'medium' | 'low';
  readBy: string[];
  createdAt: string;
  createdBy: string;
  authorName?: string | null;
  authorPhotoUrl?: string | null;
  images?: string[];
  offer?: {
    transactionType?: 'sprzedaz' | 'wynajem';
    price?: number;
    area?: number;
    rooms?: number;
    location?: string;
  };
}

export interface Reminder {
  id: string;
  user_id: string;           // ID użytkownika
  title: string;             // Tytuł przypomnienia
  description?: string;      // Opcjonalny opis
  dueDate?: string;         // Opcjonalna data wykonania (ISO string)
  completed: boolean;        // Czy wykonane
  createdAt: string;        // Data utworzenia
  updatedAt: string;        // Data aktualizacji
  priority?: 'high' | 'medium' | 'low'; // Priorytet
  notificationSent?: boolean; // Czy powiadomienie zostało wysłane (raz i tylko raz)
  notificationSentAt?: string; // Kiedy powiadomienie zostało wysłane
  relatedTo?: {
    type: 'offer' | 'client' | 'note' | 'event';
    id: string;
    label: string;
  };
}

// Create a helper function to create a chat between two users
export const createChat = async (userId1: string, userId2: string, userName1: string, userName2: string) => {
  try {
    const chatRef = collection(db, 'chats');
    const chatData = {
      participants: [userId1, userId2],
      participantNames: {
        [userId1]: userName1,
        [userId2]: userName2
      },
      isGroup: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    return await addDoc(chatRef, chatData);
  } catch (error) {
    console.error('Error creating chat:', error);
    throw error;
  }
};

export interface UserProfile {
  id: string;
  displayName: string;
  photoURL: string;
  email: string;
  phone?: string;
  location?: string;
  role?: string;
  bio?: string;
  created_at: Date;
  updated_at: Date;
}
